import React, { useCallback, useEffect, useState } from 'react';
import { clinicalAPI, visitAPI, visitMediaAPI } from '../../services/api';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { SmartDateInput } from '../../components/ss/SmartDateInput';
import { downloadBlobResponse, readBlobError } from '../../utils/download';

// ── Vitals badge ──────────────────────────────────────────────────────────────
const VitalBadge = ({ label, value, unit, loinc, warn }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{
      display: 'inline-block', padding: '5px 11px',
      background: warn ? '#fff1f2' : '#fdeef5',
      border: `1px solid ${warn ? '#fca5a5' : '#f0c8da'}`,
      borderRadius: 8, marginRight: 6, marginBottom: 6,
    }}>
      <div style={{ fontSize: 10, color: warn ? '#b91c1c' : '#0369a1', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: warn ? '#b91c1c' : '#2b1b22' }}>
        {value} <span style={{ fontSize: 10, color: '#8b7681' }}>{unit}</span>
      </div>
      {loinc && <div style={{ fontSize: 9, color: '#a99aa0' }}>LOINC: {loinc}</div>}
    </div>
  );
};

// ── Review status pill ────────────────────────────────────────────────────────
const ReviewBadge = ({ status }) => {
  const map = {
    PENDING_REVIEW:     { bg: '#fef3c7', color: '#92400e', label: 'Pending Review' },
    CHANGES_REQUESTED:  { bg: '#fee2e2', color: '#b91c1c', label: 'Changes Requested' },
    SIGNED_OFF:         { bg: '#d1fae5', color: '#065f46', label: 'Signed Off' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#475569', label: status };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

// ── Condition chip ────────────────────────────────────────────────────────────
const ConditionChip = ({ condition }) => {
  const map = {
    CRITICAL:     { bg: '#fee2e2', color: '#b91c1c' },
    DETERIORATING:{ bg: '#fed7aa', color: '#9a3412' },
    STABLE:       { bg: '#d1fae5', color: '#065f46' },
    IMPROVING:    { bg: '#dbeafe', color: '#1e40af' },
    DISCHARGED:   { bg: '#f1f5f9', color: '#475569' },
    VOIDED:       { bg: '#f1f5f9', color: '#a99aa0' },
  };
  const s = map[condition] || { bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {condition}
    </span>
  );
};

// ── Wound summary line ────────────────────────────────────────────────────────
const WoundSummary = ({ wound }) => {
  if (!wound) return null;
  const dims = [wound.length_cm, wound.width_cm, wound.depth_cm].filter(Boolean);
  return (
    <div style={{ padding: '8px 12px', background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12, marginBottom: 8 }}>
      <span style={{ fontWeight: 700, color: '#9a3412' }}>Wound: </span>
      {dims.length > 0 && <span>{dims.join(' × ')} cm · </span>}
      {wound.tissue_type && <span>{wound.tissue_type} · </span>}
      {wound.drainage && wound.drainage !== 'NONE' && <span>Drainage: {wound.drainage} · </span>}
      {wound.infection_signs && <span style={{ color: '#b91c1c', fontWeight: 700 }}>Infection signs · </span>}
      {wound.healing_pct !== undefined && <span>Healing {wound.healing_pct}%</span>}
    </div>
  );
};

// ── Section header ────────────────────────────────────────────────────────────
const SectionHead = ({ label }) => (
  <div style={{ fontSize: 11, fontWeight: 800, color: '#5e4a53', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10, marginTop: 4 }}>
    {label}
  </div>
);

// ── Field input helpers ───────────────────────────────────────────────────────
const inp = (value, onChange, opts = {}) => (
  <input
    type={opts.type || 'text'}
    step={opts.step}
    min={opts.min}
    max={opts.max}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={opts.placeholder}
    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ece3e7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}
  />
);

const ta = (value, onChange, rows = 3, placeholder = '') => (
  <textarea
    value={value}
    onChange={e => onChange(e.target.value)}
    rows={rows}
    placeholder={placeholder}
    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
  />
);

// ── Default form state ────────────────────────────────────────────────────────
const DEF_CREATE = {
  visit_id: '',
  patient_condition: 'STABLE',
  // Vitals
  blood_pressure_sys: '', blood_pressure_dia: '',
  heart_rate: '', temperature: '', oxygen_saturation: '',
  blood_sugar: '', weight_kg: '', height_cm: '',
  respiratory_rate: '', pain_scale: '',
  // Clinical text
  observations: '',
  medications_administered: '',
  procedures_performed: '',
  notes: '',
  // SOAP
  subjective: '',
  plan_text: '',
  // Wound
  wound_present: false,
  wound_length_cm: '', wound_width_cm: '', wound_depth_cm: '',
  wound_drainage: 'NONE',
  wound_tissue_type: 'GRANULATING',
  wound_infection_signs: false,
  wound_healing_pct: 0,
  // Escalation
  escalation_required: false,
  escalation_reason: '',
  // Follow-up
  next_visit_required: false,
  recommended_next_date: '',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 };
const fieldWrap = { marginBottom: 14 };

export default function ClinicalReports() {
  const [reports, setReports]     = useState([]);
  const [visits, setVisits]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);   // full report detail
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters]     = useState({ condition: '', review_status: '', date_from: '', date_to: '', escalation_only: false });
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(DEF_CREATE);
  const [editItem, setEditItem]   = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [voidItem, setVoidItem]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [pdfBusy, setPdfBusy]     = useState(false);
  const [pdfToast, setPdfToast]   = useState(null); // { kind: 'error'|'success', text }
  const [reviewBusy, setReviewBusy] = useState(false);
  // Visit attachments (photos + signature) shown as part of the report.
  const [media, setMedia]         = useState([]);
  const [mediaUrls, setMediaUrls] = useState({});   // media_id -> object URL

  const PER_PAGE = 50;

  const showPdfToast = (kind, text) => {
    setPdfToast({ kind, text });
    setTimeout(() => setPdfToast(null), 5000);
  };

  const downloadReportPdf = async (report) => {
    if (!report?.report_id) return;
    setPdfBusy(true);
    try {
      const res = await clinicalAPI.reportPdf(report.report_id);
      downloadBlobResponse(res, `clinical-report-${report.report_id}.pdf`);
      showPdfToast('success', 'Report PDF downloaded.');
    } catch (e) {
      const status = e.response?.status;
      const body = e.response?.data instanceof Blob ? await readBlobError(e.response.data) : e.response?.data;
      if (status === 503) {
        showPdfToast('error', body?.detail || 'PDF engine unavailable — please try again later.');
      } else {
        showPdfToast('error', body?.detail || 'Could not generate the report PDF.');
      }
    } finally {
      setPdfBusy(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: PER_PAGE };
    if (filters.condition) params.condition = filters.condition;
    if (filters.review_status) params.review_status = filters.review_status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.escalation_only) params.escalation_only = true;
    clinicalAPI.listReports(params)
      .then(r => { setReports(r.data || r || []); setTotal(r.total || 0); })
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    visitAPI.list({ limit: 200, status: 'COMPLETED' })
      .then(r => setVisits(r.data || []))
      .catch(() => {});
  }, []);

  const openDetail = async (row) => {
    setDetailLoading(true);
    setSelected(row);
    setMedia([]);
    // Release previous blob URLs before loading new ones.
    setMediaUrls(prev => { Object.values(prev).forEach(URL.revokeObjectURL); return {}; });
    try {
      const full = await clinicalAPI.getReportById(row.report_id);
      // unwrap() returns the API envelope — the report lives under .data
      // (storing the envelope left every detail field undefined, which is
      // why the panel showed "No vitals recorded" and no patient name).
      const report = full?.data || full;
      setSelected(report);
      loadMedia(report?.visit_id);
    } catch {
      // Keep the list-level data if detail fetch fails
      loadMedia(row?.visit_id);
    } finally {
      setDetailLoading(false);
    }
  };

  // The nurse's wound photos + the patient's sign-off signature are PHI and
  // stream through an authorised endpoint — blob them so <img> can render.
  const loadMedia = async (visitId) => {
    if (!visitId) return;
    try {
      const r = await visitMediaAPI.list(visitId);
      const items = r?.data || [];
      setMedia(items);
      const urls = {};
      await Promise.all(items.map(async m => {
        try {
          const blob = await visitMediaAPI.fileBlob(visitId, m.media_id);
          urls[m.media_id] = URL.createObjectURL(blob.data);
        } catch { /* one broken file must not hide the rest */ }
      }));
      setMediaUrls(prev => { Object.values(prev).forEach(URL.revokeObjectURL); return urls; });
    } catch {
      setMedia([]);
    }
  };

  // Doctor/admin electronic sign-off — part of the legal record.
  const handleReview = async (action) => {
    if (!selected) return;
    let notes = null;
    if (action === 'request_changes') {
      notes = window.prompt('What should the nurse change? (sent with the request)');
      if (notes === null || !notes.trim()) return;   // cancelled / empty
    } else if (!window.confirm('Sign off this report? Your sign-off becomes part of the legal record.')) {
      return;
    }
    setReviewBusy(true);
    try {
      await clinicalAPI.reviewReport(selected.report_id, action, notes);
      const full = await clinicalAPI.getReportById(selected.report_id);
      setSelected(full?.data || full);
      fetchReports();
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail : 'Review action failed');
    } finally {
      setReviewBusy(false);
    }
  };

  // ── Create ──────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createForm.visit_id) { setError('Please select a visit'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        visit_id: createForm.visit_id,
        patient_condition: createForm.patient_condition || undefined,
        observations: createForm.observations || undefined,
        notes: createForm.notes || undefined,
        subjective: createForm.subjective || undefined,
        plan_text: createForm.plan_text || undefined,
        next_visit_required: Boolean(createForm.next_visit_required),
        recommended_next_date: createForm.recommended_next_date || undefined,
        escalation_required: createForm.escalation_required || undefined,
        escalation_reason: createForm.escalation_reason || undefined,
      };
      // Vitals
      const numFields = [
        ['blood_pressure_sys', 0], ['blood_pressure_dia', 0],
        ['heart_rate', 0], ['temperature', 1], ['oxygen_saturation', 1],
        ['blood_sugar', 1], ['weight_kg', 2], ['height_cm', 1],
        ['respiratory_rate', 0], ['pain_scale', 0],
      ];
      numFields.forEach(([k]) => {
        if (createForm[k] !== '') payload[k] = Number(createForm[k]);
      });
      // Lists
      if (createForm.medications_administered)
        payload.medications_administered = createForm.medications_administered.split(',').map(s => s.trim()).filter(Boolean);
      if (createForm.procedures_performed)
        payload.procedures_performed = createForm.procedures_performed.split(',').map(s => s.trim()).filter(Boolean);
      // Wound
      if (createForm.wound_present) {
        payload.wound = {
          length_cm: createForm.wound_length_cm ? Number(createForm.wound_length_cm) : null,
          width_cm: createForm.wound_width_cm ? Number(createForm.wound_width_cm) : null,
          depth_cm: createForm.wound_depth_cm ? Number(createForm.wound_depth_cm) : null,
          drainage: createForm.wound_drainage,
          tissue_type: createForm.wound_tissue_type,
          infection_signs: Boolean(createForm.wound_infection_signs),
          healing_pct: Number(createForm.wound_healing_pct),
        };
      }
      await clinicalAPI.submitReport(payload);
      setShowCreate(false); setCreateForm(DEF_CREATE); load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit report');
    } finally { setSaving(false); }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const openEdit = (r) => {
    setEditItem(r);
    const w = r.wound || {};
    setEditForm({
      patient_condition: r.patient_condition || '',
      observations: r.observations || '',
      notes: r.notes || '',
      subjective: r.subjective || '',
      plan_text: r.plan_text || '',
      next_visit_required: r.next_visit_required || false,
      recommended_next_date: r.recommended_next_date || '',
      escalation_required: r.escalation_required || false,
      escalation_reason: r.escalation_reason || '',
      respiratory_rate: r.vitals?.respiratory_rate ?? '',
      pain_scale: r.vitals?.pain_scale ?? '',
      wound_present: !!r.wound,
      wound_length_cm: w.length_cm ?? '',
      wound_width_cm: w.width_cm ?? '',
      wound_depth_cm: w.depth_cm ?? '',
      wound_drainage: w.drainage || 'NONE',
      wound_tissue_type: w.tissue_type || 'GRANULATING',
      wound_infection_signs: w.infection_signs || false,
      wound_healing_pct: w.healing_pct ?? 0,
    });
    setError('');
  };

  const handleEditSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        patient_condition: editForm.patient_condition || undefined,
        observations: editForm.observations || undefined,
        notes: editForm.notes || undefined,
        subjective: editForm.subjective || undefined,
        plan_text: editForm.plan_text || undefined,
        next_visit_required: editForm.next_visit_required,
        recommended_next_date: editForm.recommended_next_date || undefined,
        escalation_required: editForm.escalation_required || undefined,
        escalation_reason: editForm.escalation_reason || undefined,
      };
      if (editForm.respiratory_rate !== '') payload.respiratory_rate = Number(editForm.respiratory_rate);
      if (editForm.pain_scale !== '') payload.pain_scale = Number(editForm.pain_scale);
      if (editForm.wound_present) {
        payload.wound = {
          length_cm: editForm.wound_length_cm ? Number(editForm.wound_length_cm) : null,
          width_cm: editForm.wound_width_cm ? Number(editForm.wound_width_cm) : null,
          depth_cm: editForm.wound_depth_cm ? Number(editForm.wound_depth_cm) : null,
          drainage: editForm.wound_drainage,
          tissue_type: editForm.wound_tissue_type,
          infection_signs: Boolean(editForm.wound_infection_signs),
          healing_pct: Number(editForm.wound_healing_pct),
        };
      }
      await clinicalAPI.updateReport(editItem.report_id, payload);
      setEditItem(null);
      load();
      if (selected?.report_id === editItem.report_id) openDetail(editItem);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update report');
    } finally { setSaving(false); }
  };

  // ── Void ────────────────────────────────────────────────────────────────────
  const handleVoid = async () => {
    setSaving(true);
    try {
      await clinicalAPI.voidReport(voidItem.report_id);
      setVoidItem(null);
      if (selected?.report_id === voidItem.report_id) setSelected(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to void report');
    } finally { setSaving(false); }
  };

  const cf = (k, v) => setCreateForm(p => ({ ...p, [k]: v }));
  const ef = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  // Vitals thresholds for warning display
  const warnVital = (key, val) => {
    if (!val) return false;
    const v = Number(val);
    if (key === 'temperature') return v > 38.5;
    if (key === 'blood_pressure_sys') return v > 180;
    if (key === 'blood_pressure_dia') return v > 120;
    if (key === 'oxygen_saturation') return v < 90;
    if (key === 'heart_rate') return v > 120 || v < 40;
    return false;
  };

  const conditionColor = (c) => {
    if (c === 'CRITICAL' || c === 'DETERIORATING') return '#b91c1c';
    if (c === 'STABLE' || c === 'IMPROVING') return '#065f46';
    return '#475569';
  };

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>Clinical Reports</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
            SOAP documentation · LOINC vitals · Wound assessment · ICD-10 · OASIS-E aligned
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setCreateForm(DEF_CREATE); setError(''); }}
          style={{ padding: '9px 18px', background: '#94335a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + Submit Report
        </button>
      </div>

      {/* ── Filters (server-side) ───────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 10, padding: '14px 18px', marginBottom: 18, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Condition</label>
          <select value={filters.condition} onChange={e => { setFilters(p => ({ ...p, condition: e.target.value })); setPage(1); }}
            style={{ padding: '7px 10px', border: '1px solid #ece3e7', borderRadius: 7, fontSize: 13, minWidth: 140 }}>
            <option value="">All conditions</option>
            {['STABLE','IMPROVING','DETERIORATING','CRITICAL','DISCHARGED'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Review Status</label>
          <select value={filters.review_status} onChange={e => { setFilters(p => ({ ...p, review_status: e.target.value })); setPage(1); }}
            style={{ padding: '7px 10px', border: '1px solid #ece3e7', borderRadius: 7, fontSize: 13, minWidth: 160 }}>
            <option value="">All statuses</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="CHANGES_REQUESTED">Changes Requested</option>
            <option value="SIGNED_OFF">Signed Off</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <SmartDateInput value={filters.date_from} onChange={e => { setFilters(p => ({ ...p, date_from: e.target.value })); setPage(1); }} placeholder="From date" />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <SmartDateInput value={filters.date_to} onChange={e => { setFilters(p => ({ ...p, date_to: e.target.value })); setPage(1); }} placeholder="To date" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
          <input type="checkbox" id="esc-only" checked={filters.escalation_only}
            onChange={e => { setFilters(p => ({ ...p, escalation_only: e.target.checked })); setPage(1); }}
            style={{ width: 15, height: 15 }} />
          <label htmlFor="esc-only" style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c', cursor: 'pointer' }}>Escalations only</label>
        </div>
        <button onClick={() => { setFilters({ condition: '', review_status: '', date_from: '', date_to: '', escalation_only: false }); setPage(1); }}
          style={{ padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: '#5e4a53', fontWeight: 600 }}>
          Clear
        </button>
      </div>

      {/* ── Main grid ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '380px 1fr' : '1fr', gap: 20 }}>

        {/* List */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
              Reports <span style={{ fontSize: 12, color: '#a99aa0', fontWeight: 400 }}>({total})</span>
            </h3>
            {total > PER_PAGE && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                  style={{ padding: '3px 8px', border: '1px solid #ece3e7', borderRadius: 5, background: '#fafafa', fontSize: 12, cursor: 'pointer' }}>‹</button>
                <span style={{ fontSize: 12, color: '#8b7681', alignSelf: 'center' }}>{page}</span>
                <button onClick={() => setPage(p => p+1)} disabled={page * PER_PAGE >= total}
                  style={{ padding: '3px 8px', border: '1px solid #ece3e7', borderRadius: 5, background: '#fafafa', fontSize: 12, cursor: 'pointer' }}>›</button>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ color: '#a99aa0', fontSize: 13 }}>Loading reports…</div>
          ) : reports.length === 0 ? (
            <div style={{ color: '#a99aa0', textAlign: 'center', padding: '30px 0', fontSize: 13 }}>No clinical reports found.</div>
          ) : (
            <div style={{ maxHeight: 580, overflowY: 'auto' }}>
              {reports.map(r => (
                <div key={r.report_id}
                  onClick={() => openDetail(r)}
                  style={{
                    padding: '11px 13px',
                    border: `2px solid ${selected?.report_id === r.report_id ? '#94335a' : '#f1f5f9'}`,
                    borderRadius: 8, marginBottom: 5, cursor: 'pointer',
                    background: selected?.report_id === r.report_id ? '#fdeef5' : '#fafafa',
                    opacity: r.patient_condition === 'VOIDED' ? 0.55 : 1,
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.patient_condition && <ConditionChip condition={r.patient_condition} />}
                      {r.escalation_required && (
                        <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', padding: '2px 7px', borderRadius: 8, fontWeight: 700 }}>
                          🚨 ESCALATION
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 10, color: '#a99aa0', whiteSpace: 'nowrap' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#2b1b22', marginBottom: 2 }}>
                    {r.patient_name || 'Patient'}
                  </div>
                  <div style={{ fontSize: 11, color: '#8b7681', marginBottom: 5 }}>
                    {r.nurse_name ? `by ${r.nurse_name}` : ''}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ReviewBadge status={r.review_status} />
                    <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                      {r.patient_condition !== 'VOIDED' && (
                        <>
                          <button onClick={() => openEdit(r)}
                            style={{ padding: '3px 9px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                          <button onClick={() => setVoidItem(r)}
                            style={{ padding: '3px 9px', background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Void</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflowY: 'auto', maxHeight: '85vh' }}>
            {detailLoading ? (
              <div style={{ color: '#a99aa0', fontSize: 13 }}>Loading full report…</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#2b1b22', marginBottom: 4 }}>
                      {selected.patient_name || 'Patient'}
                      {selected.nurse_name && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#8b7681', marginLeft: 8 }}>
                          · report by {selected.nurse_name}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                      {selected.patient_condition && <ConditionChip condition={selected.patient_condition} />}
                      <ReviewBadge status={selected.review_status} />
                    </div>
                    <div style={{ fontSize: 11, color: '#a99aa0' }}>
                      {selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => downloadReportPdf(selected)} disabled={pdfBusy}
                      style={{ padding: '5px 12px', background: '#fff', color: '#7b1e3c', border: '1.5px solid #7b1e3c', borderRadius: 6, fontSize: 12, cursor: pdfBusy ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: pdfBusy ? 0.6 : 1 }}>
                      {pdfBusy ? 'Preparing…' : '⬇ Download report PDF'}
                    </button>
                    {selected.patient_condition !== 'VOIDED' && (
                      <>
                        <button onClick={() => openEdit(selected)}
                          style={{ padding: '5px 12px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                        <button onClick={() => setVoidItem(selected)}
                          style={{ padding: '5px 12px', background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Void</button>
                      </>
                    )}
                    <button onClick={() => setSelected(null)}
                      style={{ background: '#f1f5f9', border: 'none', fontSize: 18, cursor: 'pointer', color: '#a99aa0', padding: '4px 8px', borderRadius: 6 }}>×</button>
                  </div>
                </div>

                {/* PDF download feedback */}
                {pdfToast && (
                  <div style={{
                    background: pdfToast.kind === 'error' ? '#fdecea' : '#f0fdf4',
                    border: `1px solid ${pdfToast.kind === 'error' ? '#ef9a9a' : '#86efac'}`,
                    color: pdfToast.kind === 'error' ? '#b71c1c' : '#15803d',
                    borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{pdfToast.kind === 'error' ? '⚠️' : '✅'}</span>
                    <span style={{ flex: 1 }}>{pdfToast.text}</span>
                    <button onClick={() => setPdfToast(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                  </div>
                )}

                {/* Escalation Banner */}
                {selected.escalation_required && (
                  <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 13 }}>🚨 Clinical Escalation Required</div>
                    {selected.escalation_reason && <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 4 }}>{selected.escalation_reason}</div>}
                  </div>
                )}

                {/* Review actions — the reason "Pending Review" exists */}
                {selected.review_status !== 'SIGNED_OFF' && selected.patient_condition !== 'VOIDED' && (
                  <div style={{ background: '#fdf6f9', border: '1px solid #f0c8da', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 12, color: '#5e4a53', fontWeight: 600, flex: 1, minWidth: 180 }}>
                      This report awaits clinical review.
                    </div>
                    <button onClick={() => handleReview('sign_off')} disabled={reviewBusy}
                      style={{ padding: '7px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: reviewBusy ? 'not-allowed' : 'pointer' }}>
                      {reviewBusy ? 'Working…' : '✓ Sign off'}
                    </button>
                    <button onClick={() => handleReview('request_changes')} disabled={reviewBusy}
                      style={{ padding: '7px 16px', background: '#fff', color: '#b45309', border: '1.5px solid #f59e0b', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: reviewBusy ? 'not-allowed' : 'pointer' }}>
                      Request changes
                    </button>
                  </div>
                )}

                {/* Visit photos + patient signature (part of the report record) */}
                {media.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <SectionHead label="Photos & Patient Signature" />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      {media.filter(m => m.media_kind !== 'SIGNATURE').map(m => (
                        <a key={m.media_id} href={mediaUrls[m.media_id]} target="_blank" rel="noreferrer"
                           title={m.caption || 'Visit photo — click to open full size'}>
                          {mediaUrls[m.media_id]
                            ? <img src={mediaUrls[m.media_id]} alt={m.caption || 'Visit photo'}
                                   style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 10, border: '1px solid #ece3e7' }} />
                            : <div style={{ width: 110, height: 110, borderRadius: 10, background: '#f1f5f9' }} />}
                        </a>
                      ))}
                    </div>
                    {media.filter(m => m.media_kind === 'SIGNATURE').map(m => (
                      <div key={m.media_id} style={{ marginTop: 10, background: '#fff', border: '1px solid #ece3e7', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53' }}>PATIENT SIGNATURE</div>
                        {mediaUrls[m.media_id] &&
                          <img src={mediaUrls[m.media_id]} alt="Patient signature"
                               style={{ height: 46, maxWidth: 220, objectFit: 'contain' }} />}
                        {m.created_at && <div style={{ fontSize: 10, color: '#a99aa0' }}>{new Date(m.created_at).toLocaleString()}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Review notes */}
                {selected.review_notes && (
                  <div style={{ background: selected.review_status === 'SIGNED_OFF' ? '#f0fdf4' : '#fef9f0', border: `1px solid ${selected.review_status === 'SIGNED_OFF' ? '#86efac' : '#fed7aa'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53', marginBottom: 4 }}>DOCTOR REVIEW NOTES</div>
                    <div style={{ fontSize: 12, color: '#374151' }}>{selected.review_notes}</div>
                    {selected.reviewed_at && <div style={{ fontSize: 10, color: '#a99aa0', marginTop: 4 }}>{new Date(selected.reviewed_at).toLocaleString()}</div>}
                  </div>
                )}

                {/* Vitals */}
                <SectionHead label="Vital Signs (LOINC-coded)" />
                <div style={{ marginBottom: 16 }}>
                  <VitalBadge label="Systolic BP" value={selected.vitals?.blood_pressure_sys} unit="mmHg" loinc="8480-6" warn={warnVital('blood_pressure_sys', selected.vitals?.blood_pressure_sys)} />
                  <VitalBadge label="Diastolic BP" value={selected.vitals?.blood_pressure_dia} unit="mmHg" loinc="8462-4" warn={warnVital('blood_pressure_dia', selected.vitals?.blood_pressure_dia)} />
                  <VitalBadge label="Heart Rate" value={selected.vitals?.heart_rate} unit="/min" loinc="8867-4" warn={warnVital('heart_rate', selected.vitals?.heart_rate)} />
                  <VitalBadge label="Temperature" value={selected.vitals?.temperature} unit="°C" loinc="8310-5" warn={warnVital('temperature', selected.vitals?.temperature)} />
                  <VitalBadge label="SpO₂" value={selected.vitals?.oxygen_saturation} unit="%" loinc="2708-6" warn={warnVital('oxygen_saturation', selected.vitals?.oxygen_saturation)} />
                  <VitalBadge label="Glucose" value={selected.vitals?.blood_sugar} unit="mmol/L" loinc="15074-8" />
                  <VitalBadge label="Weight" value={selected.vitals?.weight_kg} unit="kg" loinc="29463-7" />
                  <VitalBadge label="Height" value={selected.vitals?.height_cm} unit="cm" loinc="8302-2" />
                  <VitalBadge label="BMI" value={selected.vitals?.bmi} unit="kg/m²" loinc="39156-5" />
                  <VitalBadge label="Resp Rate" value={selected.vitals?.respiratory_rate} unit="/min" loinc="9279-1" />
                  {selected.vitals?.pain_scale !== null && selected.vitals?.pain_scale !== undefined && (
                    <VitalBadge label="Pain" value={selected.vitals.pain_scale} unit="/10" loinc="72514-3" warn={selected.vitals.pain_scale >= 7} />
                  )}
                  {!selected.vitals?.blood_pressure_sys && !selected.vitals?.heart_rate && (
                    <div style={{ color: '#a99aa0', fontSize: 12 }}>No vitals recorded</div>
                  )}
                </div>

                {/* SOAP Notes */}
                {(selected.subjective || selected.observations || selected.plan_text) && (
                  <>
                    <SectionHead label="SOAP Clinical Notes" />
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                      {selected.subjective && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 3 }}>S — SUBJECTIVE (Patient Reported)</div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selected.subjective}</div>
                        </div>
                      )}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 3 }}>O — OBJECTIVE (Vitals above)</div>
                      </div>
                      {selected.observations && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 3 }}>A — ASSESSMENT (Observations)</div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selected.observations}</div>
                        </div>
                      )}
                      {selected.plan_text && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 3 }}>P — PLAN</div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selected.plan_text}</div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Wound */}
                {selected.wound && (
                  <>
                    <SectionHead label="Wound Assessment" />
                    <div style={{ background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {selected.wound.length_cm && <div><span style={{ fontSize: 11, color: '#8b7681' }}>L</span> <strong>{selected.wound.length_cm} cm</strong></div>}
                        {selected.wound.width_cm && <div><span style={{ fontSize: 11, color: '#8b7681' }}>W</span> <strong>{selected.wound.width_cm} cm</strong></div>}
                        {selected.wound.depth_cm && <div><span style={{ fontSize: 11, color: '#8b7681' }}>D</span> <strong>{selected.wound.depth_cm} cm</strong></div>}
                        {selected.wound.drainage && <div><span style={{ fontSize: 11, color: '#8b7681' }}>Drainage</span> <strong>{selected.wound.drainage}</strong></div>}
                        {selected.wound.tissue_type && <div><span style={{ fontSize: 11, color: '#8b7681' }}>Tissue</span> <strong>{selected.wound.tissue_type}</strong></div>}
                        {selected.wound.healing_pct !== undefined && (
                          <div style={{ width: '100%' }}>
                            <div style={{ fontSize: 11, color: '#8b7681', marginBottom: 4 }}>Healing Progress</div>
                            <div style={{ background: '#fed7aa', borderRadius: 6, overflow: 'hidden', height: 12 }}>
                              <div style={{ width: `${selected.wound.healing_pct}%`, background: '#16a34a', height: '100%', transition: 'width 0.3s' }} />
                            </div>
                            <div style={{ fontSize: 11, color: '#9a3412', marginTop: 2 }}>{selected.wound.healing_pct}% healed</div>
                          </div>
                        )}
                      </div>
                      {selected.wound.infection_signs && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: '#fee2e2', borderRadius: 6, fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                          ⚠ Infection signs present
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Medications & Procedures */}
                {selected.medications_administered?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <SectionHead label="Medications Administered" />
                    {selected.medications_administered.map((m, i) => (
                      <div key={i} style={{ padding: '5px 10px', background: '#f0fdf4', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                        {typeof m === 'string' ? m : `${m.name} · ${m.dose} ${m.unit}`}
                      </div>
                    ))}
                  </div>
                )}
                {selected.procedures_performed?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <SectionHead label="Procedures Performed" />
                    {selected.procedures_performed.map((p, i) => (
                      <div key={i} style={{ padding: '5px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                        {typeof p === 'string' ? p : p.name}
                      </div>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {selected.notes && (
                  <div style={{ marginBottom: 14 }}>
                    <SectionHead label="Additional Notes" />
                    <div style={{ fontSize: 13, color: '#5e4a53', background: '#f8fafc', padding: '10px 14px', borderRadius: 8, lineHeight: 1.6 }}>{selected.notes}</div>
                  </div>
                )}

                {/* Follow-up */}
                <div style={{ padding: '8px 14px', background: selected.next_visit_required ? '#fef3c7' : '#d1fae5', borderRadius: 8, fontSize: 12, fontWeight: 600, color: selected.next_visit_required ? '#92400e' : '#065f46' }}>
                  {selected.next_visit_required ? '📅 Follow-up visit required' : '✅ No follow-up required'}
                  {selected.recommended_next_date && <span style={{ marginLeft: 8, fontWeight: 400 }}>({new Date(selected.recommended_next_date).toLocaleDateString()})</span>}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          Create / Submit Report Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showCreate && (
        <Modal title="Submit Clinical Report" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

            {/* Row 1: Visit + Condition */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 22 }}>
              <div>
                <label style={labelStyle}>Visit *</label>
                <select value={createForm.visit_id} onChange={e => cf('visit_id', e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                  <option value="">Select completed visit…</option>
                  {visits.map(v => (
                    <option key={v.visit_id} value={v.visit_id}>
                      {v.visit_id?.slice(0, 14)}… — {v.patient_name || v.patient_id?.slice(0, 10)} — {v.scheduled_start ? new Date(v.scheduled_start).toLocaleDateString() : ''}
                    </option>
                  ))}
                </select>
                {visits.length === 0 && (
                  <input value={createForm.visit_id} onChange={e => cf('visit_id', e.target.value)}
                    placeholder="Paste visit UUID…"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, marginTop: 6, boxSizing: 'border-box' }} />
                )}
              </div>
              <div>
                <label style={labelStyle}>Patient Condition</label>
                <select value={createForm.patient_condition} onChange={e => cf('patient_condition', e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                  {['STABLE','IMPROVING','DETERIORATING','CRITICAL','DISCHARGED'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Vitals (left) + Medications/Procedures (right) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 22 }}>
              <div>
                <SectionHead label="Vital Signs (leave blank to skip)" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    ['BP Systolic',     'blood_pressure_sys', 'mmHg',    50,  300, 1],
                    ['BP Diastolic',    'blood_pressure_dia', 'mmHg',    30,  200, 1],
                    ['Heart Rate',      'heart_rate',         '/min',    20,  300, 1],
                    ['Temperature',     'temperature',        '°C',      30,  45,  0.1],
                    ['SpO₂',           'oxygen_saturation',  '%',       50,  100, 0.1],
                    ['Blood Glucose',   'blood_sugar',        'mmol/L',  0.5, 40,  0.1],
                    ['Weight',          'weight_kg',          'kg',      1,   500, 0.1],
                    ['Height',          'height_cm',          'cm',      20,  300, 0.1],
                    ['Respiratory Rate','respiratory_rate',   '/min',    4,   80,  1],
                    ['Pain Scale',      'pain_scale',         '0–10',    0,   10,  1],
                  ].map(([label, key, unit, minV, maxV, step]) => (
                    <div key={key}>
                      <label style={{ ...labelStyle, color: warnVital(key, createForm[key]) ? '#b91c1c' : '#5e4a53' }}>
                        {label} <span style={{ color: '#a99aa0', fontWeight: 400 }}>({unit})</span>
                      </label>
                      <input type="number" step={step} min={minV} max={maxV}
                        value={createForm[key]}
                        onChange={e => cf(key, e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: `1px solid ${warnVital(key, createForm[key]) ? '#fca5a5' : '#ece3e7'}`, borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <SectionHead label="Medications & Procedures" />
                {[
                  ['Medications Administered', 'medications_administered', 'Comma-separated: Paracetamol 500mg, Amoxicillin 250mg'],
                  ['Procedures Performed', 'procedures_performed', 'Comma-separated: Wound dressing, IV insertion, Catheter care'],
                  ['Observations', 'observations', 'Objective clinical observations…'],
                  ['Notes', 'notes', 'Additional clinical notes…'],
                ].map(([label, key, ph]) => (
                  <div key={key} style={fieldWrap}>
                    <label style={labelStyle}>{label}</label>
                    {ta(createForm[key], v => cf(key, v), 2, ph)}
                  </div>
                ))}
              </div>
            </div>

            {/* Row 3: SOAP Notes */}
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
              <SectionHead label="SOAP Clinical Narrative" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ ...labelStyle, color: '#0369a1' }}>S — Subjective <span style={{ fontWeight: 400, color: '#a99aa0' }}>(what patient reports)</span></label>
                  {ta(createForm.subjective, v => cf('subjective', v), 3, 'Symptoms, concerns, pain description, patient complaints…')}
                </div>
                <div>
                  <label style={{ ...labelStyle, color: '#d97706' }}>P — Plan <span style={{ fontWeight: 400, color: '#a99aa0' }}>(next steps)</span></label>
                  {ta(createForm.plan_text, v => cf('plan_text', v), 3, 'Continue current care / adjust treatment / review on date / refer to doctor…')}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#a99aa0', marginTop: 8 }}>O (Objective) = vitals above · A (Assessment) = Observations + Condition above</div>
            </div>

            {/* Row 4: Wound Assessment */}
            <div style={{ background: '#fffbeb', border: '1px solid #fed7aa', borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: createForm.wound_present ? 14 : 0 }}>
                <input type="checkbox" id="wound-present" checked={createForm.wound_present}
                  onChange={e => cf('wound_present', e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer' }} />
                <label htmlFor="wound-present" style={{ fontSize: 13, fontWeight: 700, color: '#9a3412', cursor: 'pointer' }}>Wound Assessment Present</label>
              </div>
              {createForm.wound_present && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                  {[
                    ['Length (cm)', 'wound_length_cm'], ['Width (cm)', 'wound_width_cm'], ['Depth (cm)', 'wound_depth_cm'],
                  ].map(([label, key]) => (
                    <div key={key}>
                      <label style={labelStyle}>{label}</label>
                      {inp(createForm[key], v => cf(key, v), { type: 'number', step: '0.1', min: '0' })}
                    </div>
                  ))}
                  <div>
                    <label style={labelStyle}>Drainage</label>
                    <select value={createForm.wound_drainage} onChange={e => cf('wound_drainage', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ece3e7', borderRadius: 7, fontSize: 13 }}>
                      {['NONE','SEROUS','SEROSANGUINEOUS','SANGUINEOUS','PURULENT'].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Tissue Type</label>
                    <select value={createForm.wound_tissue_type} onChange={e => cf('wound_tissue_type', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ece3e7', borderRadius: 7, fontSize: 13 }}>
                      {['GRANULATING','EPITHELIALIZING','SLOUGHING','NECROTIC','INFECTED'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Healing % (0–100)</label>
                    <input type="range" min="0" max="100" value={createForm.wound_healing_pct}
                      onChange={e => cf('wound_healing_pct', e.target.value)}
                      style={{ width: '100%' }} />
                    <div style={{ fontSize: 11, color: '#9a3412', textAlign: 'center' }}>{createForm.wound_healing_pct}%</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" id="inf-signs" checked={createForm.wound_infection_signs}
                      onChange={e => cf('wound_infection_signs', e.target.checked)} style={{ width: 15, height: 15 }} />
                    <label htmlFor="inf-signs" style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', cursor: 'pointer' }}>Infection signs present</label>
                  </div>
                </div>
              )}
            </div>

            {/* Row 5: Escalation + Follow-up */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: createForm.escalation_required ? 10 : 0 }}>
                  <input type="checkbox" id="escalate" checked={createForm.escalation_required}
                    onChange={e => cf('escalation_required', e.target.checked)} style={{ width: 15, height: 15 }} />
                  <label htmlFor="escalate" style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>Flag for Escalation</label>
                </div>
                {createForm.escalation_required && (
                  <div>
                    <label style={labelStyle}>Escalation Reason</label>
                    {ta(createForm.escalation_reason, v => cf('escalation_reason', v), 2, 'Reason the doctor or hospital needs to review…')}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#8b7681', marginTop: 6 }}>
                  Auto-flagged if: Temp &gt; 38.5, BP &gt; 180/120, SpO₂ &lt; 90, or Condition = CRITICAL/DETERIORATING
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input type="checkbox" id="cr-nvr" checked={createForm.next_visit_required}
                    onChange={e => cf('next_visit_required', e.target.checked)} style={{ width: 15, height: 15 }} />
                  <label htmlFor="cr-nvr" style={{ fontSize: 13, fontWeight: 600, color: '#5e4a53', cursor: 'pointer' }}>Follow-up visit required</label>
                </div>
                {createForm.next_visit_required && (
                  <div>
                    <label style={labelStyle}>Recommended Next Date</label>
                    <SmartDateInput value={createForm.recommended_next_date || ''}
                      onChange={e => cf('recommended_next_date', e.target.value)} placeholder="Select date" />
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCreate(false)}
                style={{ flex: 1, padding: 11, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving}
                style={{ flex: 2, padding: 11, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
                {saving ? 'Submitting…' : 'Submit Clinical Report'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Edit Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {editItem && (
        <Modal title="Edit Clinical Report" onClose={() => setEditItem(null)} width={620}>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>Patient Condition</label>
              <select value={editForm.patient_condition} onChange={e => ef('patient_condition', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                <option value="">— No change —</option>
                {['STABLE','IMPROVING','DETERIORATING','CRITICAL','DISCHARGED'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Respiratory Rate (/min)</label>
              {inp(editForm.respiratory_rate, v => ef('respiratory_rate', v), { type: 'number' })}
            </div>
          </div>

          <div style={fieldWrap}>
            <label style={{ ...labelStyle, color: '#0369a1' }}>S — Subjective (patient reported)</label>
            {ta(editForm.subjective, v => ef('subjective', v), 2, 'Symptoms, complaints…')}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Observations</label>
            {ta(editForm.observations, v => ef('observations', v), 2, 'Clinical observations…')}
          </div>
          <div style={fieldWrap}>
            <label style={{ ...labelStyle, color: '#d97706' }}>P — Plan (next steps)</label>
            {ta(editForm.plan_text, v => ef('plan_text', v), 2, 'Continue care / adjust / refer…')}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Notes</label>
            {ta(editForm.notes, v => ef('notes', v), 2)}
          </div>

          {/* Wound */}
          <div style={{ borderTop: '1px solid #ece3e7', paddingTop: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: editForm.wound_present ? 12 : 0 }}>
              <input type="checkbox" id="edit-wound" checked={editForm.wound_present}
                onChange={e => ef('wound_present', e.target.checked)} style={{ width: 15, height: 15 }} />
              <label htmlFor="edit-wound" style={{ fontSize: 13, fontWeight: 700, color: '#9a3412', cursor: 'pointer' }}>Wound Assessment</label>
            </div>
            {editForm.wound_present && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[['L (cm)', 'wound_length_cm'], ['W (cm)', 'wound_width_cm'], ['D (cm)', 'wound_depth_cm']].map(([label, key]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    {inp(editForm[key], v => ef(key, v), { type: 'number', step: '0.1' })}
                  </div>
                ))}
                <div>
                  <label style={labelStyle}>Healing %</label>
                  <input type="range" min="0" max="100" value={editForm.wound_healing_pct}
                    onChange={e => ef('wound_healing_pct', e.target.value)} style={{ width: '100%' }} />
                  <div style={{ fontSize: 11, textAlign: 'center', color: '#9a3412' }}>{editForm.wound_healing_pct}%</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" id="edit-inf" checked={editForm.wound_infection_signs}
                    onChange={e => ef('wound_infection_signs', e.target.checked)} style={{ width: 14, height: 14 }} />
                  <label htmlFor="edit-inf" style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Infection signs</label>
                </div>
              </div>
            )}
          </div>

          {/* Escalation */}
          <div style={{ borderTop: '1px solid #ece3e7', paddingTop: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: editForm.escalation_required ? 10 : 0 }}>
              <input type="checkbox" id="edit-esc" checked={editForm.escalation_required}
                onChange={e => ef('escalation_required', e.target.checked)} style={{ width: 15, height: 15 }} />
              <label htmlFor="edit-esc" style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>Flag for Escalation</label>
            </div>
            {editForm.escalation_required && (
              <div style={fieldWrap}>
                <label style={labelStyle}>Escalation Reason</label>
                {ta(editForm.escalation_reason, v => ef('escalation_reason', v), 2, 'Reason for escalation…')}
              </div>
            )}
          </div>

          {/* Follow-up */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <input type="checkbox" id="edit-nvr" checked={editForm.next_visit_required}
              onChange={e => ef('next_visit_required', e.target.checked)} style={{ width: 15, height: 15 }} />
            <label htmlFor="edit-nvr" style={{ fontSize: 13, fontWeight: 600, color: '#5e4a53', cursor: 'pointer' }}>Follow-up visit required</label>
          </div>
          {editForm.next_visit_required && (
            <div style={fieldWrap}>
              <label style={labelStyle}>Recommended Date</label>
              <SmartDateInput value={editForm.recommended_next_date || ''}
                onChange={e => ef('recommended_next_date', e.target.value)} placeholder="Select date" />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={() => setEditItem(null)}
              style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
            <button onClick={handleEditSave} disabled={saving}
              style={{ flex: 2, padding: 10, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Void Confirm */}
      {voidItem && (
        <ConfirmDialog
          message="Void this clinical report?"
          detail="The report will be marked VOIDED and remain in the database for HIPAA audit compliance."
          confirmLabel="Void Report"
          loading={saving}
          onConfirm={handleVoid}
          onCancel={() => setVoidItem(null)}
        />
      )}
    </div>
  );
}
